import { type CheckinType, RESTRICTION_ERRORS, type RestrictionErrorMinimal } from 'libs/domain';

import type {
  PsDialogData,
  PsDialogResponse,
  PsSummaryErrorsData,
  PsSummaryModalData,
} from '@app/src/app/modules/people-selector';
import { CloseTimeoutDuration } from '@app/src/app/shared/components/system-alert-modal';

/**
 * Helper to handle showing check-in errors and bypass dialog
 * @param studentCollection Collection of all student data
 * @param successPeopleHashes List of successfully checked-in people hashes
 * @param opts Options for handling check-in errors
 * @param opts.errors List of errors encountered during check-in
 * @param opts.reasonName Name of the reason for the check-in
 * @param opts.canBypassError Whether user can bypass the error and check in anyway
 * @returns
 */
export const handleCheckInError = async (
  studentCollection: Array<{
    personHash: string;
    displayName: string;
    profileImageUrl?: string;
  }>,
  successPeopleHashes: string[],
  opts: {
    errors: RestrictionErrorMinimal[];
    reasonName: string;
    canBypassError?: boolean;
    checkType?: CheckinType;
    showProfilePicture?: boolean;
    openDialogHandler: (data: PsDialogData) => Promise<PsDialogResponse>;
    summaryDialogHandler: (data: PsSummaryModalData) => Promise<string[]>;
  },
): Promise<string[]> => {
  const {
    errors,
    reasonName,
    canBypassError = false,
    checkType = 'in',
    showProfilePicture = false,
    openDialogHandler,
    summaryDialogHandler,
  } = opts;

  // Single error, no successes: show error dialog for one student
  if (errors.length === 1 && successPeopleHashes.length === 0) {
    const { personHash, code } = errors[0];
    const firstPerson = studentCollection[0];
    const title = firstPerson.displayName;
    const profilePicture = showProfilePicture && firstPerson?.profileImageUrl;

    const dialogResult = await openDialogHandler({
      type: 'error',
      audioAlerts: true,
      title,
      subTitle: RESTRICTION_ERRORS[code].title || `Check ${checkType} denied`,
      message: reasonName,
      canContinue: canBypassError,
      extendedMessage:
        RESTRICTION_ERRORS[code].message || 'Some unknown error has occurred while trying to process your request',
      profilePicture,
    });

    return dialogResult?.action === 'continue' ? [personHash] : [];
  }

  // Multiple errors or partial success: show summary dialog
  const successSummary = successPeopleHashes.map(hash => {
    const student = studentCollection.find(s => s.personHash === hash);
    return {
      hash: student.personHash,
      name: student.displayName,
      status: 'success',
      reasons: [`Checked ${checkType}`],
    } as PsSummaryErrorsData;
  });

  const errorsSummary = errors.map(({ personHash, code }) => {
    const student = studentCollection.find(s => s.personHash === personHash);
    return {
      hash: personHash,
      name: student.displayName,
      status: 'error',
      reasons: [RESTRICTION_ERRORS[code].title],
    } as PsSummaryErrorsData;
  });

  const selectedHashes = await summaryDialogHandler({
    message: `could not be checked ${checkType}`,
    title: `Check ${checkType} summary`,
    subMessage: !canBypassError ? '' : `Select the people you want to “Check ${checkType}”`,
    submitLabel: `Check ${checkType}`,
    errorsData: [...successSummary, ...errorsSummary],
    displayOnly: !canBypassError,
  });

  return selectedHashes.length ? selectedHashes : [];
};

/**
 * Helper to handle showing a success dialog for single vs multiple user check-ins.
 * @param studentCollection collection of all student data
 * @param dialogHandler dialog handler to show the dialog
 * @param opts
 * @returns
 */
export const showCheckInSuccessDialog = async (
  studentCollection: Array<{
    personHash: string;
    displayName: string;
    profileImageUrl?: string;
    filename?: string;
  }>,
  dialogHandler: (data: PsDialogData) => Promise<PsDialogResponse>,
  opts: {
    showProfilePicture: boolean;
    message: string;
    checkType?: 'in' | 'out';
  },
) => {
  if (!studentCollection.length) return;

  const { message, checkType = 'in' } = opts;
  const hasManyPeople = studentCollection.length > 1;
  const showProfilePicture = opts.showProfilePicture && !hasManyPeople;
  const firstPerson = studentCollection[0];
  const profilePicture = showProfilePicture && (firstPerson?.filename || firstPerson?.profileImageUrl);

  return await dialogHandler({
    type: 'success',
    audioAlerts: true,
    title: hasManyPeople ? undefined : `${firstPerson.displayName}`,
    subTitle: hasManyPeople ? `${studentCollection.length} people checked ${checkType}` : `Checked ${checkType}`,
    message,
    closeTimeout: profilePicture ? CloseTimeoutDuration.MEDIUM : CloseTimeoutDuration.SHORT,
    profilePicture,
  });
};
